import React, {useState} from "react";
import {Alert, Dialog, DialogContent, Snackbar, ThemeProvider, useTheme} from "@mui/material";
import {useAxios} from "../../shared/technical/rest/AxiosProvider";
import {useTranslation} from "react-i18next";
import {ProcessDialogHeader} from "../../shared/components/dialogs/ProcessDialogHeader";
import {muiDataTableTheme} from "../../shared/components/tables/muiDataTableTheme";
import {SecondaryButton} from "../../shared/components/buttons/SecondaryButton";
import {downloadLocalFile} from "../../shared/technical/file-utils/downloadLocalFile";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {importUsersFromCSVFile} from "../../shared/technical/file-utils/uploadFile";
import {Download, UploadFile} from "@mui/icons-material";
import {palette} from "../../shared/technical/style/theme";
import {StyledEngineProvider} from "@mui/material/styles";
import {AxiosError} from "axios";
import {User} from "./User";

export function CSVFileUsersImportDialog(props: {
  open: boolean,
  handleClose: (event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => void,
}) {
  const theme = useTheme();
  const axios = useAxios();
  const {t} = useTranslation();
  const [importErrors, setImportErrors] = useState<string[]>();
  const [importedUsers, setImportedUsers] = useState<string[]>();
  const [snackBarImportErrors, setSnackBarImportErrors] = useState<string>();
  const {handleClose} = props;

  function closeDialog() {
    setImportErrors(undefined)
    setImportedUsers(undefined)
    handleClose()
  }

  function alert(alertColor: 'success' | 'error', message: string, users: string[] | undefined, elements: string[]) {
    return (
      <Alert
        style={{marginTop: '16px'}}
        severity={alertColor}
      >
        {
          message + ` ${users ? users.length : 0} Users have been imported.`
        }
        {
          alertColor === 'error' &&
          <ul style={{marginTop: '6px'}}>
            {elements.map((element, index) => <li key={index}>{element + '!'}</li>)}
          </ul>
        }
      </Alert>
    )
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"md"}
      open={props.open}
      onClose={closeDialog}
      aria-labelledby="CSV import users dialog"
      PaperProps={{
        sx: {
          maxHeight: '60%',
          maxWidth: '700px',
          overflow: 'hidden',
        }
      }}
    >
      <DialogContent sx={{
        backgroundColor: theme.palette.background.paper,
      }}
      >
        <ProcessDialogHeader
          title={t('CSVUsersImport.title')}
          handleClose={closeDialog}
        />
        <StyledEngineProvider>
          <ThemeProvider theme={muiDataTableTheme}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'end',
              justifyContent: 'space-around',
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '-webkit-fill-available',
                padding: "30px",
                paddingTop: "0px"
              }}>
                <Typography
                  align="center"
                  variant={"caption"}
                  sx={{paddingBottom: '10px'}}
                >
                  {t('CSVUsersImport.btnDownloadDescription')}
                </Typography>
                <SecondaryButton
                  title={t('CSVUsersImport.btnDownload')}
                  startIcon={<Download/>}
                  size="medium"
                  onClick={() => {
                    downloadLocalFile("/files/users-import.csv", "users-import.csv")
                  }}
                />
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '-webkit-fill-available',
                padding: "30px"
              }}>
                <Typography
                  align="center"
                  variant={"caption"}
                  sx={{paddingBottom: '10px'}}
                >
                  {t('CSVUsersImport.btnImportDescription')}
                </Typography>
                <input
                  accept=".csv"
                  style={{display: "none"}}
                  id="raised-button-file"
                  type="file"
                  onChange={(e) => {
                    const file = e.target?.files?.[0];
                    if (file == null) {
                      return;
                    }
                    importUsersFromCSVFile(axios, file)
                      .then((createdUsers: User[]) => {
                        setImportedUsers(createdUsers.map(user => user.email));
                        setImportErrors(undefined)
                      })
                      .catch((error: AxiosError) => {
                        const fieldErrors = error.response?.data.fieldErrors;
                        const numOfErrors = fieldErrors !== null ? fieldErrors.length : 0;
                        const errorList: string[] = new Array(numOfErrors);
                        let errors = "";
                        for (let i = 0; i < numOfErrors; i++) {
                          const message = fieldErrors[i].defaultMessage;
                          errors += message + "\n";
                          errorList[i] = message;
                          console.log(message)
                        }
                        console.warn('Could not import user! Details: ' + errors);
                        setImportErrors(errorList);
                        setImportedUsers(undefined);
                        setSnackBarImportErrors(old => (old || '') + errors);
                      });
                  }
                  }
                />
                <label htmlFor="raised-button-file">
                  <Button
                    size="medium"
                    color="secondary"
                    variant={"outlined"}
                    startIcon={<UploadFile/>}
                    sx={{
                      '&:hover': {
                        background: theme.palette.secondary.light,
                      },
                      '&:not([disabled])': {
                        background: palette.primary,
                        color: theme.palette.background.default,
                      }
                    }}
                  >
                    {t('CSVUsersImport.btnImport')}
                  </Button>
                  <Snackbar
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    open={!!snackBarImportErrors}
                    autoHideDuration={3000}
                    onClose={() => setSnackBarImportErrors(undefined)}
                  >
                    <Alert severity={'error'}>{t('CSVUsersImport.msgImportFailed')}</Alert>
                  </Snackbar>
                </label>
              </div>
            </div>
          </ThemeProvider>
        </StyledEngineProvider>
        {
          importErrors &&
          importErrors.length > 0 &&
          alert('error', t('CSVUsersImport.msgImportFailed'), [], importErrors)
        }
        {
          importedUsers &&
          importedUsers.length > 0 &&
          alert('success', t('CSVUsersImport.msgImportSuccess'), importedUsers, [])
        }
      </DialogContent>
    </Dialog>
  );
}
